/* eslint-disable arrow-body-style, consistent-return, array-callback-return, prefer-const */

import { get, isArray, isEmpty, isNil, isObject } from 'lodash'
import { amountDisplay } from '../../../../formats'
import {
  calculateIncludedTax,
  getCountrySalesTax,
  getProductChildMap,
  precise_round_to_number,
  processProducts,
  summaryScreenConstants,
} from '../../../../functions'
import { generateInstalments } from '../../../../utils/instalments'

export default function useRegistrationData({
  formData,
  formType,
  orderItems,
  feeConfigurations,
  paymentProvider,
  paymentGateway,
  appliedVouchers,
  appliedDiscounts,
  useInstalments,
  formSettings,
  displayPriceAsZero = false,
  insuranceQuote,
}) {
  const paidProductsByMember = {}
  let tfcError = {
    error: false,
    errorMessage: null,
  }
  const {
    form,
    pageName,
    formQuestions,
    organisation,
    productGroupList: productGroups,
    productList,
    products,
    subEvents,
    isTeamRego,
    registeredTeam,
  } = formData

  const childProductMap = getProductChildMap(productList)
  const gateway = paymentProvider || paymentGateway[0]

  const includeInsurance = !!insuranceQuote?.isProtected

  let commonSource
  switch ((formType || '').toLowerCase()) {
    case 'event':
      commonSource = summaryScreenConstants.EVENT_FORM
      break
    case 'shop':
      commonSource = summaryScreenConstants.SHOP_FORM
      break
    default:
      commonSource = summaryScreenConstants.MEMBERSHIP_FORM
  }

  const processedProductsData = processProducts(commonSource, orderItems, feeConfigurations, {
    id: gateway.id || gateway.sfid,
    name: gateway.name,
    parentId: gateway.parentId,
  })

  const { processedProducts, error } = processedProductsData
  // biome-ignore lint/complexity/useOptionalChain: TODO
  if (error && error.error) {
    tfcError = {
      error: true,
      errorMessage: error.errorMessage,
    }
  } else {
    tfcError = {
      error: false,
      errorMessage: null,
    }
  }

  const processQuestions = questions =>
    Object.keys(questions || {})
      .filter(id => {
        if (!((isArray(questions[id]) || isObject(questions[id])) && questions[id].length === 0)) {
          return !isNil(questions[id])
        }
      })
      .map(id => {
        const question = formQuestions.find(q => q.relatedQuestion.sfid === id)

        const answer = {
          formQuestionId: question.id,
          id,
          type: question.relatedQuestion.type,
        }

        switch (question.relatedQuestion.type) {
          case 'Picklist':
            answer.answer = questions[id].value || questions[id]
            break
          case 'multiPicklist':
            answer.answer = Object.keys(questions[id]).join(';')
            break
          case 'Document Upload': {
            const { key, format, size } = questions[id][questions[id].length - 1]

            answer.documentName =
              get(get(question, 'relatedQuestion'), 'name') || get(get(questions[id], 'file'), 'name')
            answer.documentDescription = ''
            answer.documentUrl = key
            answer.documentFormat = format
            answer.documentSize = size

            break
          }
          default:
            answer.answer = questions[id]
        }

        if (
          question.relatedQuestion.isSportsTGQuestion &&
          (question.relatedQuestion.name.includes('Phone') ||
            question.relatedQuestion.name === 'Emergency Contact Number')
        ) {
          if (typeof answer.answer === 'object') {
            answer.answer = answer.answer.value
          }
        }

        return answer
      })

  const newReg = Object.keys(formData.registrations).reduce((acc, r) => {
    if (r !== null && r !== 'null') {
      acc.push(formData.registrations[r])
    }
    return acc
  }, [])

  const registrations = {
    form: formData.form,
    members: Object.values(newReg).reduce((a, r) => {
      const memberId = r.sfid || r.id
      paidProductsByMember[memberId] = r.products
      const memberVoucher = appliedVouchers[memberId]
      const { appliedVoucher, provider } = memberVoucher || {}
      const member = {
        id: memberId,
        birthdate: r.birthdate,
        email: r.email,
        firstName: r.firstname || r.firstName,
        lastName: r.lastname || r.lastName,
        mobilePhone: r.mobile || r.mobilePhone,
        gender: r.gender,
        genderIdentity: r.genderIdentity,
        genderIdentityOther: r.genderIdentityOther,
        photo: r.photo || r.profileImageUrl,
        memberProfileId: r.relatedMemberProfileId || r.memberProfileId,
        isNewMember: r.isNewMember,
        voucherCode: appliedVoucher || null,
        voucherProvider: provider || null,
      }
      member.memberTypes = (r.memberTypes || []).map(mt => {
        return {
          id: mt.sfid,
          name: mt.name,
          isPlayer: mt.isPlayer,
          isEventParticipant: mt.isEventParticipant,
        }
      })

      member.products =
        isEmpty(processedProducts) || isNil(processedProducts)
          ? []
          : processedProducts.filter(item => item.memberId === member.id)

      member.questions = processQuestions(r.questions || {})

      a.push(member)
      return a
    }, []),
    organisation: formData.organisation.sfid,
    userid: formData.userId,
    source: commonSource,
  }

  const { teamDetails } = registeredTeam || {}

  const teamRegistrations = {
    action: !isTeamRego ? 'join' : 'register',
    form: formData.form,
    team: [
      {
        id: teamDetails?.id || formData.userId,
        name: teamDetails?.name,
        products: isEmpty(processedProducts) || isNil(processedProducts) ? [] : processedProducts,
        questions: processQuestions(registeredTeam.teamQuestions || {}),
      },
    ],
    teamDetails,
    teamProducts: isEmpty(processedProducts) || isNil(processedProducts) ? [] : processedProducts,
    teamQuestions: processQuestions(registeredTeam.teamQuestions || {}),
    organisation: formData.organisation.sfid,
    userid: formData.userId,
    source: commonSource,
  }

  const sessions = {
    loggedInUser: formData.userId,
    form,
    source: pageName,
    products: Object.values(
      registrations.members.reduce((a, r) => {
        ;(r.products || []).forEach(p => {
          if (!(p.id in a)) {
            a[p.id] = {
              id: p.id,
              quantity: 0,
              formProductId: products[p.id].formProductId,
            }
          }
          a[p.id].quantity = precise_round_to_number(a[p.id].quantity + Number(p.quantity), 2)
        })
        return a
      }, [])
    ),
  }

  const { currencyCode } = organisation

  const getInitialLoadItems = member => {
    return member.products
      .sort((a, b) => a.type - b.type)
      .map(p => {
        const currentValue = p.totalPrice
        let required = products[p.id].isMandatory || p.isMandatory || false
        const { relatedProductGroup } = products[p.id]
        if (relatedProductGroup) {
          const productGroup = productGroups.find(pg => pg.id === relatedProductGroup)
          required = Number(get(productGroup, 'minimumProductCount')) > 0
        }

        const { voucher, productPromoCode } = p
        const { id: promoCode, promoCodeAmount } = productPromoCode || {}

        return {
          name: p.name,
          id: p.id,
          formProductId: p.formProductId,
          quantity: p.quantity,
          orgId: p.orgId,
          type: p.type,
          isForSubEvent: p.isForSubEvent,
          required,
          productData: p,
          ...(voucher && { ...voucher }),
          ...(p.type === 'Add-on' && {
            deliveryMethod: p.deliveryMethod,
            image: p.image,
            sku: p.sku,
            productAttributes: p.productAttributes,
            attributeId: p.attributeId,
            optionIds: p.optionIds,
          }),
          ...(p.type === 'Event' &&
            p.subType === 'Event Entry' &&
            p.attributeValue && {
              productAttributes: p.productAttributes,
              attributeValue: p.attributeValue,
            }),
          items: [
            {
              flag: summaryScreenConstants.GENERAL,
              quantity: p.quantity,
              key: p.name,
              currencyCode,
              totalPrice: precise_round_to_number(p.listPrice * p.quantity, 2),
              currentValue: precise_round_to_number(currentValue, 2),
              currentValueInDisplay:
                currentValue === 0
                  ? displayPriceAsZero
                    ? amountDisplay('0.00', currencyCode)
                    : 'FREE'
                  : amountDisplay(currentValue, currencyCode),
              value:
                p.listPrice * p.quantity === 0
                  ? displayPriceAsZero
                    ? amountDisplay('0.00', currencyCode)
                    : 'FREE'
                  : amountDisplay(p.listPrice * p.quantity, currencyCode),
            },
            {},
            p.surcharge > 0
              ? {
                  flag: summaryScreenConstants.SURCHARGE,
                  key: 'Processing Fees',
                  totalPrice: p.surcharge,
                  currentValue: p.surcharge,
                  value: amountDisplay(p.surcharge, currencyCode),
                }
              : {},
          ],
          productDiscount: p.discount,
          ...(promoCode && {
            productPromoCode: p.productPromoCode,
            promoCode,
            promoCodeAmount,
          }),
        }
      })
  }

  const createGroupMap = member => {
    const itemIds = []
    const items = getInitialLoadItems(member)
    const mappedItems = items.reduce((a, i) => {
      a[i.formProductId] = i
      return a
    }, {})
    items.forEach(i => itemIds.push(i.formProductId))

    const subEventNames = subEvents.reduce((a, s) => {
      /* eslint-disable-next-line prefer-const */
      const prods = s.products.reduce((a, p) => {
        if (itemIds.includes(p.formProductId)) {
          a[p.formProductId] = mappedItems[p.formProductId]
        }
        return a
      }, {})
      a[s.name] = prods
      return a
    }, {})

    const addOnProducts = items.reduce((a, i) => {
      if (i.type === 'Add-on') {
        a[i.formProductId] = i
      }
      return a
    }, {})

    const teamProducts = items.reduce((a, i) => {
      if (i.type === 'Team') {
        a[i.formProductId] = i
      }
      return a
    }, {})

    const filteredItems = items.reduce((a, i) => {
      if (i.type !== 'Add-on' && i.type !== 'Team' && !i.isForSubEvent) {
        a[i.formProductId] = i
      }
      return a
    }, {})

    return isTeamRego
      ? {
          Products: filteredItems,
          ...subEventNames,
          'Team Add-ons': addOnProducts,
          'Team Products': teamProducts,
        }
      : {
          Products: filteredItems,
          ...subEventNames,
          'Add-ons': addOnProducts,
          'Team Products': teamProducts,
        }
  }
  /* eslint-disable-next-line prefer-const */
  const summaries = isTeamRego
    ? teamRegistrations.team.map(team => {
        return {
          id: team.id,
          title: team.name,
          items: getInitialLoadItems(team),
        }
      })
    : registrations.members.map(member => {
        return {
          id: member.id,
          title: [member.firstName, member.lastName].join(' '),
          items: getInitialLoadItems(member),
        }
      })

  const itemsGroupMap = (isTeamRego ? teamRegistrations.team : registrations.members).reduce((a, member) => {
    a[member.id] = createGroupMap(member)
    return a
  }, {})

  const calculateTotal = () => {
    let newSubTotals = 0
    let newFees = 0
    let voucherBalance = 0
    let discountBalance = 0
    let promoCodeBalance = 0
    let newTaxTotal = 0

    const discounts = appliedDiscounts.map(discount => {
      const {
        name,
        summary: { totalAmount },
      } = discount

      discountBalance = discountBalance + totalAmount

      return {
        name,
        rawValue: totalAmount,
      }
    })

    const orderPromoCodes = {}

    const processTotals = registration => {
      registration.products.forEach(p => {
        // biome-ignore lint/correctness/noUnusedVariables: TODO
        const { voucher, discount, productPromoCode } = p

        newSubTotals = precise_round_to_number(newSubTotals + p.listPrice * p.quantity, 2)
        newFees = precise_round_to_number(newFees + p.surcharge, 2)

        // Calculate tax per product item - tax is calculated on totalPrice (after discounts)
        if (p.gstApplicable && organisation.taxRate) {
          const { taxAmount } = calculateIncludedTax(p.totalPrice, organisation.taxRate)
          newTaxTotal = precise_round_to_number(newTaxTotal + taxAmount, 2)
        }

        if (voucher) {
          const { voucherAmount } = voucher
          voucherBalance = precise_round_to_number(voucherBalance + (voucherAmount || 0), 2)
        }

        if (productPromoCode) {
          const {
            promoCodeId,
            promoCodeAmount,
            promoCodeRule: { name },
          } = productPromoCode

          promoCodeBalance = precise_round_to_number(promoCodeBalance + (promoCodeAmount || 0), 2)

          if (!orderPromoCodes[promoCodeId]) {
            orderPromoCodes[promoCodeId] = {
              name,
              totalAmount: 0,
            }
          }

          orderPromoCodes[promoCodeId].totalAmount += promoCodeAmount
        }
      })
    }

    ;(isTeamRego ? teamRegistrations.team : registrations.members).map(reg => {
      processTotals(reg)
    })

    let newTotal = precise_round_to_number(
      newSubTotals + newFees - voucherBalance - discountBalance - promoCodeBalance,
      2
    )

    newTotal = newTotal > 0 ? newTotal : 0

    const returnData = {
      currencyCode,
      subTotal: {
        name: 'Subtotal',
        rawValue: newSubTotals,
        value:
          newSubTotals === 0
            ? displayPriceAsZero
              ? amountDisplay('0.00', currencyCode)
              : 'FREE'
            : amountDisplay(newSubTotals, currencyCode),
      },
      fees: {
        name: 'Processing Fees',
        rawValue: newFees,
        value: amountDisplay(newFees, currencyCode),
      },
      ...(voucherBalance && {
        voucher: {
          name: 'Total voucher amount applied',
          rawValue: voucherBalance,
          value: amountDisplay(voucherBalance, currencyCode),
        },
      }),
      ...(discountBalance && { discounts }),
      promoCodes: Object.values(orderPromoCodes).map(promoCode => {
        const { name, totalAmount } = promoCode
        return {
          name,
          rawValue: totalAmount,
          value: amountDisplay(totalAmount, currencyCode),
        }
      }),
      ...(newTaxTotal > 0 && {
        tax: {
          name: `Includes ${getCountrySalesTax(organisation.billingCountry || get(organisation, 'country.countryCode')) || 'Tax'}`,
          rawValue: newTaxTotal,
          value: amountDisplay(newTaxTotal, currencyCode),
        },
      }),
      ...(includeInsurance && {
        insurance: {
          name: 'Refund Protection',
          rawValue: insuranceQuote.quoteAmount,
          value: amountDisplay(insuranceQuote.quoteAmount, currencyCode),
        },
      }),
      total: {
        name: 'Total',
        rawValue: newTotal,
        value:
          newTotal === 0
            ? displayPriceAsZero
              ? amountDisplay('0.00', currencyCode)
              : 'FREE'
            : amountDisplay(newTotal, currencyCode),
      },
    }

    registrations.amount = newTotal
    registrations.surcharge = newFees
    teamRegistrations.amount = newTotal
    teamRegistrations.surcharge = newFees

    return returnData
  }

  const regTeam = {
    ...registeredTeam,
    action: !isTeamRego ? 'join' : 'register',
  }

  const paymentData = calculateTotal()

  const { instalmentsCount, instalmentsFrequency, instalmentsDelay, timezone } = formSettings
  const instalments = generateInstalments({
    totalAmount: paymentData.total.rawValue,
    instalmentsCount,
    instalmentsFrequency,
    instalmentsDelay,
    timezone,
    ...(includeInsurance && {
      additionalFirstCharge: insuranceQuote.quoteAmount,
    }),
  })

  const newPaymentData = {
    ...paymentData,
    instalment: {
      useInstalment: !!useInstalments,
      rawValue: instalments[0]?.amountRaw,
      instalments,
    },
  }

  return {
    childProductMap,
    registrations: isTeamRego ? teamRegistrations : registrations,
    paidProductsByMember,
    tfcError,
    sessions,
    currencyCode,
    summaries,
    itemsGroupMap,
    paymentData: newPaymentData,
    registeredTeam: regTeam,
    instalments,
  }
}
